import { motion } from 'framer-motion';
import { ArrowRight, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import heroBg from '../../assets/hero_datacenter.png';

const Hero = () => {
  return (
    <section className="relative min-h-[85vh] flex items-center pt-2 sm:pt-28 lg:pt-32 pb-12 sm:pb-20 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={heroBg} 
          alt="Infrastructure réseau datacenter" 
          className="w-full h-full object-cover opacity-60 dark:opacity-50" 
          fetchPriority="high"
          loading="eager"
        />
        {/* Gradients adaptatifs : clairs en mode light, sombres en mode dark */}
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg)] via-[var(--bg)]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-transparent to-[var(--bg)]/60" />
        
        {/* Ambient glows plus discrets */}
        <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full blur-[100px] opacity-20" 
             style={{ background: 'var(--accent-green)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[120px] opacity-20" 
             style={{ background: 'var(--accent-blue)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-0">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} 
            style={{ willChange: 'transform, opacity', transformPerspective: 1000, backfaceVisibility: 'hidden' }}
            className="flex items-center gap-2 mb-2 sm:mb-6">
            <div className="px-3 py-1.5 text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-widest flex items-center gap-2 border shadow-sm"
              style={{ 
                color: 'var(--accent-green)', 
                borderColor: 'var(--accent-green)', 
                background: 'var(--bg-surface)' 
              }}>
              <Activity size={12} />
              Pôle d'Expertise IT — Dakar
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{ color: 'var(--text-primary)', willChange: 'transform, opacity', transformPerspective: 1000, backfaceVisibility: 'hidden' }}
            className="tracking-tight mb-4 sm:mb-6"
          >
            Des Réseaux{' '}
            <span className="bg-gradient-to-r from-[#3f782b] to-[#165883] bg-clip-text text-transparent">Hautes Performances</span>{' '}
            pour les Entreprises.
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            style={{ willChange: 'transform, opacity', transformPerspective: 1000, backfaceVisibility: 'hidden' }}
            className="text-sm sm:text-lg md:text-xl max-w-2xl mb-8 sm:mb-10 leading-relaxed"
            style={{ color: 'var(--text-secondary)' }}>
            De la Zone Industrielle de Dakar à Diamniadio, ETCS conçoit, déploie et sécurise vos infrastructures réseaux. Continuité d'activité garantie.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            style={{ willChange: 'transform, opacity', transformPerspective: 1000, backfaceVisibility: 'hidden' }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-3.5 rounded-lg font-semibold text-black text-sm sm:text-base transition-all duration-300 group hover:-translate-y-0.5"
              style={{ background: 'var(--accent-green)' }}>
              Audit Réseau
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/services"
              className="inline-flex items-center justify-center px-6 py-3 sm:py-3.5 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300"
              style={{ border: '1px solid var(--border-hover)', color: 'var(--text-primary)' }}>
              Nos Solutions
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
            style={{ willChange: 'transform, opacity', transformPerspective: 1000, backfaceVisibility: 'hidden' }}
            className="mt-10 sm:mt-14 flex flex-wrap gap-4 sm:gap-8 pt-6 sm:pt-8" style={{ borderTop: '1px solid var(--border)' }}>
            {[
              { value: "50+", label: "Clients" },
              { value: "99%", label: "Uptime" },
              { value: "24h", label: "SAV" },
              { value: "8 ans", label: "Expertise" },
            ].map((stat) => (
              <div key={stat.label} className="flex-1 min-w-[80px]">
                <div className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stat.value}</div>
                <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'var(--text-secondary)' }}>{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
